import { useEffect, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from "react";
import { t } from "../i18n";

// Panels people resize: they drag the panel's edge, or use the arrow keys on
// it; a double-click puts it back. The width is kept in this browser.

export interface Edge {
  key: string;
  initial: number;
  min: number;
  max: number;
  // side is the panel's side of the window: the sidebar's edge is on its
  // right, so dragging right widens it; the thread's is on its left.
  side: "left" | "right";
}

export const sidebarEdge: Edge = { key: "lantern.sidebarWidth", initial: 240, min: 180, max: 480, side: "left" };
export const threadEdge: Edge = { key: "lantern.threadWidth", initial: 400, min: 320, max: 800, side: "right" };

const clamp = (edge: Edge, w: number) => Math.round(Math.min(edge.max, Math.max(edge.min, w)));

function stored(edge: Edge): number {
  try {
    const w = Number(localStorage.getItem(edge.key));
    return w ? clamp(edge, w) : edge.initial;
  } catch {
    return edge.initial;
  }
}

export function useWidth(edge: Edge) {
  const [width, setWidth] = useState(() => stored(edge));
  useEffect(() => {
    try {
      if (width === edge.initial) localStorage.removeItem(edge.key);
      else localStorage.setItem(edge.key, String(width));
    } catch {
      // Private windows may refuse; the width lasts until reload.
    }
  }, [edge, width]);
  return [width, (w: number) => setWidth(clamp(edge, w))] as const;
}

// Resizer is the edge of a panel; its parent is what the panel's side is
// measured from: the workspace for the sidebar, the thread panel itself for
// the thread.
export function Resizer({ edge, width, onResize, label, style }: { edge: Edge; width: number; onResize: (w: number) => void; label: string; style?: CSSProperties }) {
  const [dragging, setDragging] = useState(false);
  const wider = edge.side === "left" ? "ArrowRight" : "ArrowLeft";
  const narrower = edge.side === "left" ? "ArrowLeft" : "ArrowRight";

  function down(e: PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  }
  function move(e: PointerEvent<HTMLDivElement>) {
    if (!dragging) return;
    const box = e.currentTarget.parentElement!.getBoundingClientRect();
    onResize(edge.side === "left" ? e.clientX - box.left : box.right - e.clientX);
  }
  function up(e: PointerEvent<HTMLDivElement>) {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
  }
  function key(e: KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 48 : 16;
    if (e.key === wider) onResize(width + step);
    else if (e.key === narrower) onResize(width - step);
    else if (e.key === "Home") onResize(edge.min);
    else if (e.key === "End") onResize(edge.max);
    else return;
    e.preventDefault();
  }

  // While dragging, the whole page shows the resize cursor and text is not
  // selected.
  useEffect(() => {
    document.body.classList.toggle("resizing", dragging);
  }, [dragging]);

  return (
    <div
      className={`resizer ${dragging ? "dragging" : ""}`}
      style={style}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={edge.min}
      aria-valuemax={edge.max}
      aria-valuenow={width}
      tabIndex={0}
      title={t("Drag to resize; double-click to reset")}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onDoubleClick={() => onResize(edge.initial)}
      onKeyDown={key}
    />
  );
}
